/**
 * Find and replace in the editor (part 19a log, tables F and Y): the matches the find worker reported, drawn as marks
 * that follow the writer's edits until the next search, one of them the current match; and the text a replace made, put
 * in as one change the writer can undo. The search itself never runs here (principle 4). Colours are the app's: the
 * marks carry classes only.
 */
import { StateEffect, StateField } from '@codemirror/state';
import type { ChangeSet, EditorState, Extension } from '@codemirror/state';
import { Decoration, EditorView } from '@codemirror/view';
import type { DecorationSet } from '@codemirror/view';

/** The classes a match's mark carries: every match, and the current one besides. */
export const MATCH_CLASS = Object.freeze({ match: 'ct-match', current: 'ct-match-current' });

/** A range of the text. */
export interface TextRange {
  readonly from: number;
  readonly to: number;
}

/** The matches shown: their marks, and the current one, each mapped through every edit since. */
interface Shown {
  readonly marks: DecorationSet;
  readonly current: TextRange | null;
}

const matchMark = Decoration.mark({ class: MATCH_CLASS.match });
const currentMark = Decoration.mark({ class: MATCH_CLASS.current });

/** F2, F9: new matches, as a flat list `[from₀, to₀, …]` (positions in the text after the transaction); none current. */
export const showMatches = StateEffect.define<readonly number[]>();

/** F3–F5: the current match (positions in the text after the transaction), or none. */
export const setCurrentMatch = StateEffect.define<TextRange | null>();

/** A range mapped through `changes`, or null once it holds no text. */
function mapRange(range: TextRange, changes: ChangeSet): TextRange | null {
  const from = changes.mapPos(range.from, 1);
  const to = changes.mapPos(range.to, -1);
  return from < to ? { from, to } : null;
}

/** `from`–`to` clipped to a text of `length`, or null when that leaves nothing. */
function clip(from: number, to: number, length: number): TextRange | null {
  const start = Math.max(0, from);
  const end = Math.min(length, to);
  return start < end ? { from: start, to: end } : null;
}

/** The marks of `ranges` (a flat list, positions in the text after the transaction), clipped to its `length`. */
function marksOf(ranges: readonly number[], length: number): DecorationSet {
  const marks = [];
  for (let i = 0; i + 1 < ranges.length; i += 2) {
    const range = clip(Number(ranges[i]), Number(ranges[i + 1]), length);
    if (range !== null) marks.push(matchMark.range(range.from, range.to));
  }
  return Decoration.set(marks, true);
}

/** F9: the matches shown, following the writer's edits until the next search replaces them. */
export const matchField: StateField<Shown> = StateField.define<Shown>({
  create: () => ({ marks: Decoration.none, current: null }),
  update: (value, transaction) => {
    if (!transaction.docChanged && !transaction.effects.some((effect) => effect.is(showMatches) || effect.is(setCurrentMatch))) return value;
    let marks = value.marks.map(transaction.changes);
    let current = value.current === null ? null : mapRange(value.current, transaction.changes);
    for (const effect of transaction.effects) {
      if (effect.is(showMatches)) {
        marks = marksOf(effect.value, transaction.state.doc.length);
        current = null;
      } else if (effect.is(setCurrentMatch)) {
        current = effect.value === null ? null : clip(effect.value.from, effect.value.to, transaction.state.doc.length);
      }
    }
    return { marks, current };
  },
  // The current match's mark is a set of its own over the others, so moving it never rebuilds theirs.
  provide: (field) => [
    EditorView.decorations.from(field, (shown) => shown.marks),
    EditorView.decorations.from(field, (shown) => (shown.current === null ? Decoration.none : Decoration.set([currentMark.range(shown.current.from, shown.current.to)]))),
  ],
});

/** The matches shown now, mapped through every edit since they were found, as a flat list. */
export function shownMatches(state: EditorState): number[] {
  const out: number[] = [];
  const cursor = state.field(matchField).marks.iter();
  while (cursor.value !== null) {
    out.push(cursor.from, cursor.to);
    cursor.next();
  }
  return out;
}

/** The current match now, or null. */
export function currentMatch(state: EditorState): TextRange | null {
  return state.field(matchField).current;
}

/** The matches' extension. */
export const findMarks: Extension = matchField;
